import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Container, Spinner } from "@/components/shared";
import { ArrowLeft, Quote, Info, Clock, BookText, Tag, ExternalLink } from "lucide-react";

function Block({ icon: Icon, label, text }) {
  if (!text) return null;
  return (
    <div className="card-surface p-5" data-testid={`kb-block-${label}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={17} className="text-gold" strokeWidth={1.5} />
        <h3 className="font-display text-base">{label}</h3>
      </div>
      <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export default function KnowledgeDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setE(null); setError(false);
    api.get(`/knowledge/${id}`).then(({ data }) => setE(data)).catch(() => setError(true));
  }, [id]);

  if (error) return <Container><Link to="/library" className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-6"><ArrowLeft size={16} /> Bibliothèque</Link><p className="text-white/60">Fiche introuvable.</p></Container>;
  if (!e) return <Container><Spinner label="Chargement..." /></Container>;

  const isQuote = e.category === "Citations";
  return (
    <Container>
      <Link to="/library" data-testid="kb-back" className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-6"><ArrowLeft size={16} /> Bibliothèque</Link>
      <div className="mb-5">
        <p className="text-gold text-xs uppercase tracking-[0.2em] mb-3">{e.category}{e.theme ? ` · ${e.theme}` : ""}</p>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-light" data-testid="kb-title">{e.title}</h1>
        {e.author && <p className="text-white/60 mt-2">{e.author}{e.date ? ` · ${e.date}` : ""}</p>}
      </div>

      <div className="space-y-4">
        {isQuote ? (
          <div className="card-surface p-6" data-testid="kb-quote">
            <Quote size={22} className="text-gold mb-3" />
            <p className="text-white text-xl leading-relaxed italic">{e.content}</p>
          </div>
        ) : (
          <Block icon={BookText} label="Contenu" text={e.content} />
        )}
        <Block icon={Info} label="Explication" text={e.explanation} />
        <Block icon={Clock} label="Contexte historique" text={e.context} />

        {e.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center pt-1">
            <Tag size={14} className="text-white/40" />
            {e.tags.map((t) => <span key={t} className="text-xs rounded-full bg-white/[0.05] text-white/60 px-2.5 py-1">{t}</span>)}
          </div>
        )}
        {e.source && <p className="text-white/35 text-xs flex items-center gap-1.5"><ExternalLink size={12} /> Source : {e.source}</p>}
      </div>
    </Container>
  );
}
